import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const { user } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const [order, setOrder] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let active = true;

    const poll = async () => {
      if (!active) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setOrder(data);
          setState("paid");
          if (!cleared.current) {
            cleared.current = true;
            clear();
          }
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          return;
        }
      } catch {
        /* keep polling */
      }
      attempts += 1;
      if (attempts >= MAX_POLLS) {
        setState("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      active = false;
    };
  }, [sessionId, clear]);

  return (
    <div className="grid min-h-[75vh] place-items-center bg-ivory px-5" data-testid="payment-success-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-md text-center"
      >
        {state === "checking" && (
          <>
            <Loader2 size={40} className="mx-auto animate-spin text-gold" />
            <h1 className="mt-8 font-serif text-4xl font-light text-ink">Confirming your payment…</h1>
            <p className="mt-3 text-ink/60">One moment while we finalize your order.</p>
          </>
        )}

        {state === "paid" && (
          <>
            <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-gold/15 text-gold">
              <Check size={30} strokeWidth={1.5} />
            </span>
            <h1 className="mt-8 font-serif text-4xl font-light tracking-tight text-ink md:text-5xl">
              Thank you for your order
            </h1>
            <p className="mt-4 text-ink/60">
              {order?.order_number && (
                <>Your order <span className="text-ink" data-testid="order-number">{order.order_number}</span> is confirmed. </>
              )}
              A confirmation has been sent to your email. Your Noora pieces are on their way.
            </p>
            <div className="mt-10 flex justify-center gap-4">
              <Link to="/shop" className="rounded-full bg-ink px-8 py-4 text-[0.72rem] uppercase tracking-[0.24em] text-ivory transition-colors hover:bg-gold">
                Continue Shopping
              </Link>
              {user && (
                <Link to="/account" className="rounded-full border border-ink px-8 py-4 text-[0.72rem] uppercase tracking-[0.24em] text-ink transition-colors hover:bg-ink hover:text-ivory">
                  View Orders
                </Link>
              )}
            </div>
          </>
        )}

        {(state === "timeout" || state === "error") && (
          <>
            <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-beige text-ink/70">
              <AlertCircle size={28} strokeWidth={1.5} />
            </span>
            <h1 className="mt-8 font-serif text-4xl font-light text-ink">
              {state === "timeout" ? "Still processing" : "Something went wrong"}
            </h1>
            <p className="mt-4 text-ink/60">
              {state === "timeout"
                ? "Your payment is taking a little longer than usual. If you were charged, your order will appear in your account shortly."
                : "We couldn't confirm this payment. If you were charged, please contact us and we'll help right away."}
            </p>
            <div className="mt-10 flex justify-center gap-4">
              <Link to="/shop" className="rounded-full bg-ink px-8 py-4 text-[0.72rem] uppercase tracking-[0.24em] text-ivory transition-colors hover:bg-gold">
                Back to Shop
              </Link>
              <Link to="/contact" className="rounded-full border border-ink px-8 py-4 text-[0.72rem] uppercase tracking-[0.24em] text-ink transition-colors hover:bg-ink hover:text-ivory">
                Contact Us
              </Link>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}
